import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "../api/adminApi";

export function useAdminLogs({ adminToken, enabled }) {
  const [logId, setLogId] = useState("");
  const [logsUserId, setLogsUserId] = useState("");
  const [dbQuery, setDbQuery] = useState("");
  const [log, setLog] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [lastLogs, setLastLogs] = useState([]);
  const [runtimeLevel, setRuntimeLevel] = useState("");
  const [runtimeQuery, setRuntimeQuery] = useState("");
  const [runtimeLogs, setRuntimeLogs] = useState([]);
  const [runtimeLimit, setRuntimeLimit] = useState(300);
  const [runtimeAuto, setRuntimeAuto] = useState(true);
  const [runtimePaused, setRuntimePaused] = useState(false);

  const runtimeSinceRef = useRef(0);
  const runtimePollRef = useRef(null);

  const filteredLastLogs = useMemo(() => {
    const rows = Array.isArray(lastLogs) ? lastLogs : [];
    const query = String(dbQuery || "").trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((entry) => {
      const hay = `${entry?.id || ""} ${entry?.user_id || ""} ${entry?.user_question || ""} ${entry?.gpt_answer || ""}`.toLowerCase();
      return hay.includes(query);
    });
  }, [dbQuery, lastLogs]);

  const loadChart = useCallback(async (token = adminToken) => {
    if (!token) return;
    try {
      const res = await adminApi.getLogStats(token);
      setChartData({
        labels: res.data?.labels || [],
        data: res.data?.data || []
      });
    } catch (error) {
      console.error(error);
    }
  }, [adminToken]);

  const loadLastLogs = useCallback(async (token = adminToken) => {
    if (!token) return;
    try {
      const res = await adminApi.getLastLogs(token);
      setLastLogs(res.data?.logs || []);
    } catch (error) {
      console.error("Ошибка получения последних логов:", error);
    }
  }, [adminToken]);

  const loadRuntimeLogs = useCallback(async (token = adminToken, params = {}) => {
    if (!token) return;
    try {
      const res = await adminApi.getRuntimeLogs(token, { limit: runtimeLimit, ...params });
      const rows = Array.isArray(res.data?.logs) ? res.data.logs : [];
      setRuntimeLogs(rows);

      const newestTs = rows.length ? Math.max(...rows.map((row) => Number(row?.ts || 0))) : 0;
      if (newestTs > 0) runtimeSinceRef.current = newestTs + 1;
    } catch (error) {
      console.error("Ошибка получения runtime логов:", error);
      setRuntimeLogs([]);
    }
  }, [adminToken, runtimeLimit]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    loadChart(adminToken);
    loadLastLogs(adminToken);
    loadRuntimeLogs(adminToken);
  }, [enabled, adminToken, loadChart, loadLastLogs, loadRuntimeLogs]);

  const pollRuntimeLogs = useCallback(async () => {
    if (!adminToken || runtimePaused) return;
    try {
      const sinceMs = runtimeSinceRef.current || 0;
      const res = await adminApi.getRuntimeLogs(adminToken, {
        limit: runtimeLimit,
        level: runtimeLevel,
        sinceMs
      });
      const rows = Array.isArray(res.data?.logs) ? res.data.logs : [];
      if (!rows.length) return;

      const newestTs = Math.max(...rows.map((row) => Number(row?.ts || 0)));
      if (Number.isFinite(newestTs) && newestTs > runtimeSinceRef.current) {
        runtimeSinceRef.current = newestTs + 1;
      }

      setRuntimeLogs((prev) => {
        const prevRows = Array.isArray(prev) ? prev : [];
        const combined = [...rows, ...prevRows];
        const seen = new Set();
        const unique = [];
        for (const row of combined) {
          const key = `${row?.ts || 0}|${row?.level || ""}|${row?.file || ""}|${row?.line || ""}|${row?.message || ""}`;
          if (seen.has(key)) continue;
          seen.add(key);
          unique.push(row);
          if (unique.length >= Math.max(200, Number(runtimeLimit || 300))) break;
        }
        return unique;
      });
    } catch (error) {
      console.error("Runtime logs poll error:", error);
    }
  }, [adminToken, runtimeLimit, runtimeLevel, runtimePaused]);

  useEffect(() => {
    if (!enabled || !adminToken) return undefined;
    if (!runtimeAuto || runtimePaused) return undefined;

    runtimePollRef.current = setInterval(() => {
      pollRuntimeLogs();
    }, 2000);

    return () => {
      if (runtimePollRef.current) clearInterval(runtimePollRef.current);
      runtimePollRef.current = null;
    };
  }, [enabled, adminToken, pollRuntimeLogs, runtimeAuto, runtimePaused]);

  const searchLogById = useCallback(async () => {
    if (!logId || !adminToken) return;
    try {
      const res = await adminApi.getLogById(adminToken, logId);
      setLog(res.data?.log || null);
      if (!res.data?.log) {
        window.alert("Лог не найден");
      }
    } catch (error) {
      console.error(error);
      setLog(null);
      window.alert("Ошибка при поиске лога");
    }
  }, [adminToken, logId]);

  const searchLogsByUser = useCallback(async () => {
    if (!logsUserId || !adminToken) return;
    try {
      const res = await adminApi.getLogsByUser(adminToken, Number(logsUserId));
      setUserLogs(res.data?.logs || []);
    } catch (error) {
      console.error(error);
      setUserLogs([]);
      window.alert("Ошибка при получении логов пользователя");
    }
  }, [adminToken, logsUserId]);

  return {
    state: {
      logId,
      logsUserId,
      dbQuery,
      log,
      userLogs,
      chartData,
      lastLogs,
      filteredLastLogs,
      runtimeLevel,
      runtimeQuery,
      runtimeLogs,
      runtimeLimit,
      runtimeAuto,
      runtimePaused
    },
    actions: {
      setLogId,
      setLogsUserId,
      setDbQuery,
      searchLogById,
      searchLogsByUser,
      reloadChart: () => loadChart(adminToken),
      reloadLastLogs: () => loadLastLogs(adminToken),
      setRuntimeLevel,
      setRuntimeQuery,
      setRuntimeLimit,
      setRuntimeAuto,
      setRuntimePaused,
      resetRuntimeCursor: () => {
        runtimeSinceRef.current = 0;
      },
      reloadRuntimeLogs: () => {
        runtimeSinceRef.current = 0;
        return loadRuntimeLogs(adminToken, { level: runtimeLevel });
      },
      applyRuntimeFilter: () => {
        runtimeSinceRef.current = 0;
        return loadRuntimeLogs(adminToken, { level: runtimeLevel });
      },
      pollRuntimeNow: () => pollRuntimeLogs()
    }
  };
}

export default useAdminLogs;
